import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Friend, FriendLocation, FriendGroupWithMembers, TemporaryLocationWithFriend } from '../types';

interface CachedData {
  friends: Friend[] | null;
  friendLocations: FriendLocation[] | null;
  groups: FriendGroupWithMembers[] | null;
  tempLocations: TemporaryLocationWithFriend[] | null;
  lastFetch: {
    friends?: number;
    friendLocations?: number;
    groups?: number;
    tempLocations?: number;
  };
}

interface DataCacheContextType {
  cache: CachedData;
  setCachedFriends: (friends: Friend[]) => void;
  setCachedFriendLocations: (locations: FriendLocation[]) => void;
  setCachedGroups: (groups: FriendGroupWithMembers[]) => void;
  setCachedTempLocations: (locations: TemporaryLocationWithFriend[]) => void;
  getCachedFriends: () => Friend[] | null;
  getCachedFriendLocations: () => FriendLocation[] | null;
  getCachedGroups: () => FriendGroupWithMembers[] | null;
  getCachedTempLocations: () => TemporaryLocationWithFriend[] | null;
  isCacheValid: (key: keyof CachedData['lastFetch'], maxAge?: number) => boolean;
  clearCache: () => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

// Default cache validity: 5 minutes
const DEFAULT_CACHE_MAX_AGE = 5 * 60 * 1000;

export const DataCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<CachedData>({
    friends: null,
    friendLocations: null,
    groups: null,
    tempLocations: null,
    lastFetch: {},
  });

  const isCacheValid = (key: keyof CachedData['lastFetch'], maxAge: number = DEFAULT_CACHE_MAX_AGE): boolean => {
    const lastFetch = cache.lastFetch[key];
    if (!lastFetch) return false;
    return Date.now() - lastFetch < maxAge;
  };

  const setCachedFriends = (friends: Friend[]) => {
    setCache((prev) => ({
      ...prev,
      friends,
      lastFetch: { ...prev.lastFetch, friends: Date.now() },
    }));
  };

  const setCachedFriendLocations = (locations: FriendLocation[]) => {
    setCache((prev) => ({
      ...prev,
      friendLocations: locations,
      lastFetch: { ...prev.lastFetch, friendLocations: Date.now() },
    }));
  };

  const setCachedGroups = (groups: FriendGroupWithMembers[]) => {
    setCache((prev) => ({
      ...prev,
      groups,
      lastFetch: { ...prev.lastFetch, groups: Date.now() },
    }));
  };

  const setCachedTempLocations = (locations: TemporaryLocationWithFriend[]) => {
    setCache((prev) => ({
      ...prev,
      tempLocations: locations,
      lastFetch: { ...prev.lastFetch, tempLocations: Date.now() },
    }));
  };

  const getCachedFriends = () => {
    return isCacheValid('friends') ? cache.friends : null;
  };

  const getCachedFriendLocations = () => {
    return isCacheValid('friendLocations') ? cache.friendLocations : null;
  };

  const getCachedGroups = () => {
    return isCacheValid('groups') ? cache.groups : null;
  };

  const getCachedTempLocations = () => {
    return isCacheValid('tempLocations') ? cache.tempLocations : null;
  };

  const clearCache = () => {
    setCache({
      friends: null,
      friendLocations: null,
      groups: null,
      tempLocations: null,
      lastFetch: {},
    });
  };

  return (
    <DataCacheContext.Provider
      value={{
        cache,
        setCachedFriends,
        setCachedFriendLocations,
        setCachedGroups,
        setCachedTempLocations,
        getCachedFriends,
        getCachedFriendLocations,
        getCachedGroups,
        getCachedTempLocations,
        isCacheValid,
        clearCache,
      }}
    >
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = (): DataCacheContextType => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};
